import { Snackbar } from '@mui/material';
import ConfirmDeleteCategoryDialog from 'features/lead/lead-management/manage-questions-dialogs/confirm-delete-category-dialog';
import ConfirmDeleteQuestionDialog from 'features/lead/lead-management/manage-questions-dialogs/confirm-delete-question-dialog';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHref, useLocation, useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import {
  addClosedCategoryDimension,
  addClosedDimensionQuestions,
  addEffectCategoryDimension,
  addEffectDimensionQuestions,
  addParticipantToLeadTag,
  createClosedLeadQuestion,
  createEffectLeadQuestions,
  createLeadTag,
  createOpenLeadQuestions,
  deleteLeadCategory,
  deleteLeadQuestion,
  getAllClosedLeadQuestions,
  getAllEffectLeadQuestions,
  getAllOpenLeadQuestions,
  getLeadTagDetails,
  updateParticipantToLeadTag,
} from '../API/lead';
import { Routes } from '../enums';
import CreateQuestionnaireDialog from '../features/lead/lead-management/lead-manage-questionnaires/create-questionnaire-dialog';
import CloneQuestionnaireDialog from '../features/lead/lead-management/lead-questionnaire-details/create-new-questionnaire/clone-questionnaire-dialog';
import QuestionnaireCreationView from '../features/lead/lead-management/lead-questionnaire-details/create-new-questionnaire/questionnaire-creation-view';
import {
  AddClosedDimensionQuestionPayload,
  AddDimensionDialogState,
  AddEffectDimensionQuestionPayload,
  AddNewClosedQuestionDialogState,
  AddNewEffectQuestionsDialogState,
  AddNewOpenQuestionsDialogState,
  AddParticipantsDialogState,
  AddQuestionDialogState,
  ClosedQuestion,
  ConfirmDeleteLeadCategoryDialog,
  ConfirmDeleteLeadQuestionDialog,
  CreateClosedQuestionPayload,
  CreateEffectQuestionPayload,
  CreateQuestionnaireDialogState,
  DetailResponse,
  DuplicateQuestionnaireDialogState,
  EffectQuestion,
  ExtendClosedQuestionPayload,
  ExtendEffectQuestionPayload,
  OpenQuestion,
  QuestionnaireLanguages,
  ResParticipant,
  i18nQuestion,
} from '../features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import AddParticipantsDialog from '../features/lead/lead-management/lead-questionnaire-details/participants/add-participant-dialog';
import QuestionnaireDetailsView from '../features/lead/lead-management/lead-questionnaire-details/view-existing-questionnaire/questionnaire-details-view';
import AddDimensionDialog from '../features/lead/lead-management/manage-questions-dialogs/add-dimension-dialog';
import AddNewClosedQuestionDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-closed-question-dialog';
import AddNewEffectQuestionsDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-effec-question-dialog';
import AddNewOpenQuestionsDialog from '../features/lead/lead-management/manage-questions-dialogs/add-new-open-question-dialog';
import AddQuestionsDialog from '../features/lead/lead-management/manage-questions-dialogs/add-questions-dialog';
import { Perspectives } from '../features/lead/lead-questionnaire/lead-questionnaire.types';
import Logger from '../logger';

const LeadQuestionnaireDetails: React.FC = () => {
  const { t } = useTranslation();
  const { tenant } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [tagId, setTagId] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [participantsToAdd, setParticipantsToAdd] = useState<Array<{ name: string; perspective: string }>>([]);
  const [folderToAdd, setFolderToAdd] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [copyQuery, setCopyQuery] = useState<boolean>(false);
  const [details, setDetails] = useState<DetailResponse>();

  const [closedQuestions, setClosedQuestions] = useState<ClosedQuestion[]>([]);
  const [selectedClosedQuestions, setSelectedClosedQuestions] = useState<string[]>([]);
  const [preventExpansion, setPreventExpansion] = useState<boolean>(false);

  const [effectQuestions, setEffectQuestions] = useState<EffectQuestion[]>([]);
  const [selectedEffectQuestions, setSelectedEffectQuestions] = useState<string[]>([]);

  const [openQuestions, setOpenQuestions] = useState<OpenQuestion[]>([]);
  const [selectedOpenQuestions, setSelectedOpenQuestions] = useState<string[]>([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [createQuestionnaireDialog, setCreateQuestionnaireDialog] = useState<CreateQuestionnaireDialogState>({ open: false, name: '' });
  const [duplicateQuestionnaireDialog, setDuplicateQuestionnaireDialog] = useState<DuplicateQuestionnaireDialogState>({ open: false, name: '' });
  const [participantTrigger, setParticipantTrigger] = useState(true);
  const [addParticipantsDialog, setAddParticipantsDialog] = useState<AddParticipantsDialogState>({
    open: false,
    participants: [{ name: '', role: '' }],
  });
  const [addQuestionsDialog, setAddQuestionsDialog] = useState<AddQuestionDialogState>({
    open: false,
    selfAssessment: { de: '', en: '' },
    externalAssessment: { de: '', en: '' },
    categoryId: undefined,
    categorySourceId: undefined,
    subCategoryId: '',
    subCategorySourceId: undefined,
  });
  const [addNewClosedQuestionDialog, setAddNewClosedQuestionDialog] = useState<AddNewClosedQuestionDialogState>({
    open: false,
    categoryName: { de: '', en: '' },
    categoryInstructionSelf: { de: '', en: '' },
    categoryInstructionExternal: { de: '', en: '' },
    subCategories: [
      {
        name: { de: '', en: '' },
        description: { de: '', en: '' },
        questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
      },
    ],
  });
  const [addDimensionDialog, setAddDimensionDialog] = useState<AddDimensionDialogState>({
    open: false,
    categoryId: undefined,
    name: { de: '', en: '' },
    description: { de: '', en: '' },
    questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
  });
  const [addNewEffectQuestionsDialog, setAddNewEffectQuestionsDialog] = useState<AddNewEffectQuestionsDialogState>({
    open: false,
    subCategories: [
      {
        name: { de: '', en: '' },
        questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
      },
    ],
  });
  const [addNewOpenQuestionsDialog, setAddNewOpenQuestionsDialog] = useState<AddNewOpenQuestionsDialogState>({
    open: false,
    questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
  });
  const [confirmDeleteLeadQuestionDialog, setConfirmDeleteLeadQuestionDialog] = useState<ConfirmDeleteLeadQuestionDialog>({
    open: false,
    questionId: '',
  });
  const [confirmDeleteLeadCategoryDialog, setConfirmDeleteLeadCategoryDialog] = useState<ConfirmDeleteLeadCategoryDialog>({
    open: false,
    categoryId: '',
  });

  const pathname = generatePath(`${Routes.LEAD_QUESTIONNAIRE}`);
  const path = useHref(pathname);
  const dynamicRoute: string = `${window.location.origin}${path}`;

  const fetchDetails = async (id: string, preventLoading?: boolean) => {
    if (!preventLoading) {
      setLoading(true);
    }

    const res = await getLeadTagDetails(id);
    if (res && !res.error) {
      setDetails(res);
      setFolderToAdd(res.folder);
    } else if (res.error) {
      handleOpenErrorDialog(res?.error, '/lead/tags/:id', 'get');
    }
    setLoading(false);
  };

  const fetchClosedQuestions = async (duplicateQuestions?: ClosedQuestion[], isCopy?: boolean) => {
    const res = await getAllClosedLeadQuestions();
    if (res && !res.error) {
      const questionsIds: string[] = [];
      if (duplicateQuestions) {
        if (isCopy) {
          duplicateQuestions.forEach((item: ClosedQuestion) =>
            item.subCategories.forEach((subCategory) => subCategory.questions.forEach((question) => questionsIds.push(question.sourceId)))
          );
          setSelectedClosedQuestions(questionsIds);
        }
        const mergedRes: ClosedQuestion[] = duplicateQuestions;
        const currRes: ClosedQuestion[] = res;

        const duplicateCategoryIds: string[] = [];
        const duplicateSubCategoryIds: string[] = [];
        const duplicateQuestionIds: string[] = [];

        duplicateQuestions.forEach((category) => {
          duplicateCategoryIds.push(isCopy ? category.name.de : category.id);
          category.subCategories.forEach((subCategory) => {
            duplicateSubCategoryIds.push(isCopy ? subCategory.name.de : subCategory.id);
            subCategory.questions.forEach((question) => duplicateQuestionIds.push(isCopy ? question.sourceId : question.id));
          });
        });

        const updateSelectedClosedQuestions = [...selectedClosedQuestions];
        if (isCopy) {
          currRes.forEach((category) => {
            if (duplicateCategoryIds.includes(category.name.de)) {
              category.subCategories.forEach((subCategory) => {
                if (duplicateSubCategoryIds.includes(subCategory.name.de)) {
                  subCategory.questions.forEach((question) => {
                    if (duplicateQuestionIds.includes(question.id)) {
                      const findCategoryIndex = mergedRes.findIndex((dupCategory) => dupCategory.name.de === category.name.de);
                      const findSubCategoryIndex = mergedRes[findCategoryIndex].subCategories.findIndex(
                        (dupSubCategory) => dupSubCategory.name.de === subCategory.name.de
                      );
                      const findQuestion = mergedRes[findCategoryIndex].subCategories[findSubCategoryIndex].questions.find(
                        (item) => item.sourceId === question.id
                      );
                      _.pull(mergedRes[findCategoryIndex].subCategories[findSubCategoryIndex].questions, findQuestion);
                      mergedRes[findCategoryIndex].subCategories[findSubCategoryIndex].questions.push(question);
                    } else {
                      const findCategoryIndex = mergedRes.findIndex((dupCategory) => dupCategory.name.de === category.name.de);
                      const findSubCategoryIndex = mergedRes[findCategoryIndex].subCategories.findIndex(
                        (dupSubCategory) => dupSubCategory.name.de === subCategory.name.de
                      );
                      mergedRes[findCategoryIndex].subCategories[findSubCategoryIndex].questions.push(question);
                    }
                  });
                } else {
                  const findCategoryIndex = mergedRes.findIndex((dupCategory) => dupCategory.name.de === category.name.de);
                  mergedRes[findCategoryIndex].subCategories.push(subCategory);
                }
              });
            } else {
              mergedRes.push(category);
            }
          });
        } else {
          currRes.forEach((category) => {
            if (duplicateCategoryIds.includes(category.id)) {
              category.subCategories.forEach((subCategory) => {
                if (duplicateSubCategoryIds.includes(subCategory.id)) {
                  subCategory.questions.forEach((question) => {
                    if (duplicateQuestionIds.includes(question.id)) {
                      return;
                    } else {
                      const findCategoryIndex = mergedRes.findIndex((dupCategory) => dupCategory.id === category.id);
                      const findSubCategoryIndex = mergedRes[findCategoryIndex].subCategories.findIndex(
                        (dupSubCategory) => dupSubCategory.id === subCategory.id
                      );
                      mergedRes[findCategoryIndex].subCategories[findSubCategoryIndex].questions.push(question);
                      updateSelectedClosedQuestions.push(question.id);
                    }
                  });
                } else {
                  const findCategoryIndex = mergedRes.findIndex((dupCategory) => dupCategory.id === category.id);
                  mergedRes[findCategoryIndex].subCategories.push(subCategory);
                }
              });
            } else {
              mergedRes.push(category);
            }
          });
        }

        if (!isCopy) {
          setSelectedClosedQuestions(updateSelectedClosedQuestions);
        }

        setClosedQuestions(mergedRes);
      } else {
        res.forEach((item: ClosedQuestion) =>
          item.subCategories.forEach((subCategory) => subCategory.questions.forEach((question) => questionsIds.push(question.id)))
        );
        setClosedQuestions(res);
        setSelectedClosedQuestions(questionsIds);
      }
      setPreventExpansion(false);
    } else if (res.error) {
      handleOpenErrorDialog(res?.error, '/lead/closed-questions', 'get');
    }
  };

  const fetchOpenQuestions = async (duplicateQuestions?: OpenQuestion[], isCopy?: boolean) => {
    const res = await getAllOpenLeadQuestions();
    if (res && !res.error) {
      const questionsIds: string[] = [];
      if (duplicateQuestions) {
        if (isCopy) {
          duplicateQuestions.forEach((item: OpenQuestion) => questionsIds.push(item.sourceId));
          setSelectedOpenQuestions(questionsIds);
        }
        const mergedRes: OpenQuestion[] = duplicateQuestions;
        const currRes: OpenQuestion[] = res;

        const duplicateQuestionIds: string[] = [];

        duplicateQuestions.forEach((question) => {
          duplicateQuestionIds.push(isCopy ? question.sourceId : question.id);
        });

        const updateSelectedOpenQuestions = [...selectedOpenQuestions];
        currRes.forEach((question) => {
          if (duplicateQuestionIds.includes(question.id)) {
            if (isCopy) {
              const findQuestion = mergedRes.find((item) => item.sourceId === question.id);
              _.pull(mergedRes, findQuestion);
              mergedRes.push(question);
            } else {
              return;
            }
          } else {
            mergedRes.push(question);
            updateSelectedOpenQuestions.push(question.id);
          }
        });
        if (!isCopy) {
          setSelectedOpenQuestions(updateSelectedOpenQuestions);
        }

        setOpenQuestions(mergedRes);
      } else {
        res.forEach((item: OpenQuestion) => questionsIds.push(item.id));
        setSelectedOpenQuestions(questionsIds);
        setOpenQuestions(res);
      }
      setPreventExpansion(false);
    } else if (res.error) {
      handleOpenErrorDialog(res?.error, '/lead/open-questions', 'get');
    }
  };

  const fetchEffectQuestions = async (duplicateQuestions?: EffectQuestion[], isCopy?: boolean) => {
    const res = await getAllEffectLeadQuestions();
    if (res && !res.error) {
      const questionsIds: string[] = [];
      if (duplicateQuestions) {
        if (isCopy) {
          duplicateQuestions.forEach((item: EffectQuestion) => item.questions.forEach((question) => questionsIds.push(question.sourceId)));
          setSelectedEffectQuestions(questionsIds);
        }
        const mergedRes: EffectQuestion[] = duplicateQuestions;
        const currRes: EffectQuestion[] = res;

        const duplicateSubCategoryIds: string[] = [];
        const duplicateQuestionIds: string[] = [];

        duplicateQuestions.forEach((subCategory) => {
          duplicateSubCategoryIds.push(isCopy ? subCategory.name.de : subCategory.id);
          subCategory.questions.forEach((question) => {
            duplicateQuestionIds.push(isCopy ? question.sourceId : question.id);
          });
        });

        const updateSelectedEffectQuestions = [...selectedEffectQuestions];
        currRes.forEach((subCategory) => {
          if (duplicateSubCategoryIds.includes(isCopy ? subCategory.name.de : subCategory.id)) {
            subCategory.questions.forEach((question) => {
              if (duplicateQuestionIds.includes(question.id)) {
                if (isCopy) {
                  const findSubCategoryIndex = mergedRes.findIndex((dupSubCategory) =>
                    isCopy ? dupSubCategory.name.de === subCategory.name.de : dupSubCategory.id === subCategory.id
                  );
                  const findQuestion = mergedRes[findSubCategoryIndex].questions.find((item) => item.sourceId === question.id);
                  _.pull(mergedRes[findSubCategoryIndex].questions, findQuestion);
                  mergedRes[findSubCategoryIndex].questions.push(question);
                } else {
                  return;
                }
              } else {
                const findSubCategoryIndex = mergedRes.findIndex((dupSubCategory) =>
                  isCopy ? dupSubCategory.name.de === subCategory.name.de : dupSubCategory.id === subCategory.id
                );
                mergedRes[findSubCategoryIndex].questions.push(question);
                updateSelectedEffectQuestions.push(question.id);
              }
            });
          } else {
            mergedRes.push(subCategory);
          }
        });
        if (!isCopy) {
          setSelectedEffectQuestions(updateSelectedEffectQuestions);
        }

        setEffectQuestions(mergedRes);
      } else {
        res.forEach((item: EffectQuestion) => item.questions.forEach((question) => questionsIds.push(question.id)));
        setSelectedEffectQuestions(questionsIds);
        setEffectQuestions(res);
      }
      setPreventExpansion(false);
    } else if (res.error) {
      handleOpenErrorDialog(res?.error, '/lead/effect-questions', 'get');
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const id = query.get('id');
    const leadTagFolder = query.get('leadTagFolderId');
    if (id !== null) {
      setTagId(id);
      fetchDetails(id);
    }
    if (copyQuery && details && details.closedQuestions && details.openQuestions && details.effectQuestions) {
      Logger.trace('Copy Path');
      fetchClosedQuestions(details.closedQuestions, true);
      fetchOpenQuestions(details.openQuestions, true);
      fetchEffectQuestions(details.effectQuestions, true);
    }
    if (id === null && !copyQuery && leadTagFolder !== null) {
      setIsNew(true);
      setFolderToAdd(leadTagFolder);
      handleOpenCreateQuestionnaireDialog();
      fetchClosedQuestions();
      fetchOpenQuestions();
      fetchEffectQuestions();
    }
  }, [isNew, copyQuery]);

  useEffect(() => {
    if (!preventExpansion) {
      const updateExpanded = [...expanded];
      closedQuestions.forEach((category) => {
        updateExpanded.push(category.id);
        category.subCategories.forEach((subCategory) => {
          updateExpanded.push(subCategory.id);
        });
      });
      if (openQuestions.length > 0) {
        updateExpanded.push('openQuestions');
      }
      if (effectQuestions.length > 0) {
        updateExpanded.push('effectQuestions');
        effectQuestions.forEach((category) => {
          updateExpanded.push(category.id);
        });
      }
      setExpanded(updateExpanded);
    }
  }, [closedQuestions, openQuestions, effectQuestions]);

  const routeBack = () => {
    navigate(Routes.LEAD_CREATION);
  };

  const copyToClipboard = (participant: ResParticipant) => {
    return async () => {
      setSnackbarOpen(true);
      setTimeout(() => {
        setSnackbarOpen(false);
      }, 3000);
      if (!participant.completed || participant.perspective === 'self') {
        return navigator.clipboard.writeText(`${dynamicRoute}?tagId=${tagId}&participantId=${participant.id}&tenantId=${tenant}`);
      } else {
        const addedParticipant = { name: participant.name, perspective: participant.perspective };
        const res = await addParticipantToLeadTag(tagId, [addedParticipant]);
        if (!res.error) {
          fetchDetails(tagId, true);
          return navigator.clipboard.writeText(`${dynamicRoute}?tagId=${tagId}&participantId=${res[0].id}&tenantId=${tenant}`);
        } else {
          handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId/participant', 'post');
        }
      }
    };
  };

  const handleEditParticipantIsSuspended = (pid: string) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateParticipant = async () => {
        const res = await updateParticipantToLeadTag(tagId, pid, { isSuspended: !event.target.checked });
        if (!res.error) {
          fetchDetails(tagId, true);
        } else {
          handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId/participant/:participantId', 'put');
        }
      };
      updateParticipant();
    };
  };

  const handleEditParticipantRole = (pid: string) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateParticipant = async () => {
        const res = await updateParticipantToLeadTag(tagId, pid, { perspective: event.target.value });
        if (!res.error) {
          fetchDetails(tagId, true);
        } else {
          handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId/participant/:participantId', 'put');
        }
      };
      updateParticipant();
    };
  };

  const copyToClipboardAnonymous = () => {
    setSnackbarOpen(true);
    setTimeout(() => {
      setSnackbarOpen(false);
    }, 3000);

    return navigator.clipboard.writeText(`${dynamicRoute}?tagId=${tagId}&tenantId=${tenant}`);
  };

  /**
   * Create questionnaire handlers
   */

  const handleOpenCreateQuestionnaireDialog = () => {
    setCreateQuestionnaireDialog({ ...createQuestionnaireDialog, open: true });
  };

  const handleCloseCreateQuestionnaireDialog = () => {
    if (createQuestionnaireDialog.name === '') {
      routeBack();
    }
    setCreateQuestionnaireDialog({ open: false, name: '' });
  };

  const handleCreateQuestionnaireTextfieldChange = (type: 'name') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateQuestionnaireDialog = { ...createQuestionnaireDialog };
      updateQuestionnaireDialog[type] = event.target.value;
      setCreateQuestionnaireDialog(updateQuestionnaireDialog);
    };
  };

  const handleEnterName = () => {
    setNewName(createQuestionnaireDialog.name);
    const updateParticipantsToAdd = [];
    updateParticipantsToAdd.push({ name: createQuestionnaireDialog.name, perspective: Perspectives.SELF });
    setParticipantsToAdd(updateParticipantsToAdd);
    handleCloseCreateQuestionnaireDialog();
  };

  const handleSaveTag = async () => {
    const orderedSelectedClosedQuestions: string[] = [];
    closedQuestions.forEach((category: ClosedQuestion) =>
      category.subCategories.forEach((subCategory) =>
        subCategory.questions.forEach((question) => {
          if (selectedClosedQuestions.includes(question.id)) {
            orderedSelectedClosedQuestions.push(question.id);
          }
        })
      )
    );
    const orderedSelectedEffectQuestions: string[] = [];
    effectQuestions.forEach((subCategory) =>
      subCategory.questions.forEach((question) => {
        if (selectedEffectQuestions.includes(question.id)) {
          orderedSelectedEffectQuestions.push(question.id);
        }
      })
    );
    const orderedSelectedOpenQuestions: string[] = [];
    openQuestions.forEach((question) => {
      if (selectedOpenQuestions.includes(question.id)) {
        orderedSelectedOpenQuestions.push(question.id);
      }
    });
    const res = await createLeadTag(
      newName,
      orderedSelectedClosedQuestions,
      orderedSelectedOpenQuestions,
      orderedSelectedEffectQuestions,
      participantsToAdd,
      folderToAdd
    );
    if (res && !res.error) {
      navigate(`${Routes.LEAD_CREATION_DETAILS}?id=${res.id}`);
      setIsNew(false);
      setCopyQuery(false);
      setExpanded([]);
    } else {
      handleOpenErrorDialog(res?.error, '/lead/tags', 'post');
    }
  };

  /**
   * Handlers for duplicating questionnaire dialog
   */

  const handleOpenDuplicateQuestionnaireDialog = () => {
    setDuplicateQuestionnaireDialog({ ...duplicateQuestionnaireDialog, open: true });
  };

  const handleCloseDuplicateQuestionnaireDialog = () => {
    setDuplicateQuestionnaireDialog({ open: false, name: '' });
  };

  const handleDuplicateQuestionnaireDialogTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDuplicateQuestionnaireDialog({ ...duplicateQuestionnaireDialog, name: event.target.value });
  };

  const duplicateQuestionnaire = () => {
    const updateParticipantsToAdd = [];
    updateParticipantsToAdd.push({ name: duplicateQuestionnaireDialog.name, perspective: Perspectives.SELF });
    setParticipantsToAdd(updateParticipantsToAdd);
    setCopyQuery(true);
    setIsNew(true);
    setNewName(duplicateQuestionnaireDialog.name);
    handleCloseDuplicateQuestionnaireDialog();
  };

  /**
   * Handlers for adding participants
   */

  const handleOpenAddParticipantsDialog = () => {
    setAddParticipantsDialog({ ...addParticipantsDialog, open: true });
    setParticipantTrigger(!participantTrigger);
  };

  const handleCloseAddParticipantsDialog = () => {
    setAddParticipantsDialog({ open: false, questionnaire: undefined, participants: [{ name: '', role: '' }] });
    setParticipantTrigger(!participantTrigger);
  };

  const formChangeHandlerGenerator = (idx: number, type: 'name' | 'role') => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addParticipantsDialog };
      updateDialog.participants[idx][type] = e.target.value;
      setAddParticipantsDialog(updateDialog);
      setParticipantTrigger(!participantTrigger);
    };
  };

  const formChangeHandleDeleteGenerator = (idx: number) => {
    return () => {
      const updateDialog = { ...addParticipantsDialog };
      updateDialog.participants.splice(idx, 1);
      setAddParticipantsDialog(updateDialog);
      setParticipantTrigger(!participantTrigger);
    };
  };

  const addParticipant = () => {
    setAddParticipantsDialog({ ...addParticipantsDialog, participants: [...addParticipantsDialog.participants, { name: '', role: '' }] });
    setParticipantTrigger(!participantTrigger);
  };

  const handleAddParticipants = async () => {
    const participants: Array<{ name: string; perspective: string }> = addParticipantsDialog.participants.map((item) => ({
      name: item.name,
      perspective: item.role,
    }));
    if (isNew) {
      const updateParticipantsToAdd = [...participantsToAdd];
      participants.forEach((item) => updateParticipantsToAdd.push(item));
      setParticipantsToAdd(updateParticipantsToAdd);
      handleCloseAddParticipantsDialog();
    } else {
      const res = await addParticipantToLeadTag(tagId, participants);
      if (!res.error) {
        handleCloseAddParticipantsDialog();
        fetchDetails(tagId);
      } else {
        handleOpenErrorDialog(res?.error, '/lead/tags/:leadTagId/participant', 'post');
      }
    }
  };

  const getRealPerspective = (id: string) => {
    switch (id) {
      case Perspectives.SELF:
        return 'Selbsteinschätzung';
      case Perspectives.BOSS:
        return 'Führungskraft';
      case Perspectives.EMPLOYEE:
        return 'Mitarbeiter*in';
      case Perspectives.COLLEAGUE:
        return 'Kolleg*in';
      case Perspectives.OTHER:
        return 'Andere';
      default:
        return '';
    }
  };

  /**
   * Handlers for tree view
   */

  const handleToggleTreeNode = (event: any, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const handleSelectionClosedQuestionsChange = (id: string) => {
    return () => {
      const updateSelectedQuestions = [...selectedClosedQuestions];
      const foundQuestion = updateSelectedQuestions.find((question) => question === id);
      if (foundQuestion) {
        _.pull(updateSelectedQuestions, foundQuestion);
      } else {
        updateSelectedQuestions.push(id);
      }
      setSelectedClosedQuestions(updateSelectedQuestions);
    };
  };

  const handleSelectionOpenQuestionsChange = (id: string) => {
    return () => {
      const updateSelectedQuestions = [...selectedOpenQuestions];
      const foundQuestion = updateSelectedQuestions.find((question) => question === id);
      if (foundQuestion) {
        _.pull(updateSelectedQuestions, foundQuestion);
      } else {
        updateSelectedQuestions.push(id);
      }
      setSelectedOpenQuestions(updateSelectedQuestions);
    };
  };

  const handleSelectionEffectQuestionsChange = (id: string) => {
    return () => {
      const updateSelectedQuestions = [...selectedEffectQuestions];
      const foundQuestion = updateSelectedQuestions.find((question) => question === id);
      if (foundQuestion) {
        _.pull(updateSelectedQuestions, foundQuestion);
      } else {
        updateSelectedQuestions.push(id);
      }
      setSelectedEffectQuestions(updateSelectedQuestions);
    };
  };

  const handleAddQuestionToClosedCategory = async () => {
    const payload: AddClosedDimensionQuestionPayload[] = [
      {
        id: copyQuery ? addQuestionsDialog.categorySourceId || '' : addQuestionsDialog.categoryId || '',
        subCategories: [
          {
            id: copyQuery ? addQuestionsDialog.subCategorySourceId || '' : addQuestionsDialog.subCategoryId,
            questions: [
              {
                externalAssessment: { de: addQuestionsDialog.externalAssessment.de, en: addQuestionsDialog.externalAssessment.en },
                selfAssessment: { de: addQuestionsDialog.selfAssessment.de, en: addQuestionsDialog.selfAssessment.en },
              },
            ],
          },
        ],
      },
    ];
    const res = await addClosedDimensionQuestions(payload);
    if (res && !res.error) {
      fetchClosedQuestions(copyQuery ? details?.closedQuestions : closedQuestions, copyQuery);
      handleCloseAddQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/closed-questions', 'post');
    }
  };

  const handleAddQuestionToEffectCategory = async () => {
    //
    const payload: AddEffectDimensionQuestionPayload[] = [
      {
        id: copyQuery ? addQuestionsDialog.subCategorySourceId || '' : addQuestionsDialog.subCategoryId,
        questions: [
          {
            externalAssessment: { de: addQuestionsDialog.externalAssessment.de, en: addQuestionsDialog.externalAssessment.en },
            selfAssessment: { de: addQuestionsDialog.selfAssessment.de, en: addQuestionsDialog.selfAssessment.en },
          },
        ],
      },
    ];
    const res = await addEffectDimensionQuestions(payload);
    if (res && !res.error) {
      fetchEffectQuestions(copyQuery ? details?.effectQuestions : effectQuestions, copyQuery);
      handleCloseAddQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/effect-questions', 'post');
    }
  };

  const handleOpenAddQuestionsDialog = (subCategoryId: string, categoryId?: string, categorySourceId?: string, subCategorySourceId?: string) => {
    return () => {
      setAddQuestionsDialog({ ...addQuestionsDialog, open: true, categoryId, subCategoryId, categorySourceId, subCategorySourceId });
    };
  };

  const handleCloseAddQuestionsDialog = () => {
    setAddQuestionsDialog({
      open: false,
      selfAssessment: { de: '', en: '' },
      externalAssessment: { de: '', en: '' },
      categoryId: undefined,
      subCategoryId: '',
      categorySourceId: undefined,
      subCategorySourceId: undefined,
    });
  };

  const handleAddQuestionDialogTextFieldChange = (type: 'selfAssessment' | 'externalAssessment', lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateAddQuestionDialog = { ...addQuestionsDialog };
      updateAddQuestionDialog[type][lang] = event.target.value;
      setAddQuestionsDialog(updateAddQuestionDialog);
    };
  };

  /**
   * Handlers for adding new closed questions
   */

  const handleOpenAddNewClosedQuestionDialog = () => {
    setAddNewClosedQuestionDialog({ ...addNewClosedQuestionDialog, open: true });
  };

  const handleCloseAddNewClosedQuestionDialog = () => {
    setAddNewClosedQuestionDialog({
      open: false,
      categoryName: { de: '', en: '' },
      categoryInstructionSelf: { de: '', en: '' },
      categoryInstructionExternal: { de: '', en: '' },
      subCategories: [
        {
          name: { de: '', en: '' },
          description: { de: '', en: '' },
          questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
        },
      ],
    });
  };

  const handleCategoryNameChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryName: {
          ...prevState.categoryName,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const handleCategoryInstructionSelfChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryInstructionSelf: {
          ...prevState.categoryInstructionSelf,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const handleCategoryInstructionExternalChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddNewClosedQuestionDialog((prevState) => ({
        ...prevState,
        categoryInstructionExternal: {
          ...prevState.categoryInstructionExternal,
          [lang]: event.target.value,
        },
      }));
    };
  };

  const addNewSubCategory = () => {
    const updateDialog = { ...addNewClosedQuestionDialog };
    updateDialog.subCategories.push({
      name: { de: '', en: '' },
      description: { de: '', en: '' },
      questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
    });
    setAddNewClosedQuestionDialog(updateDialog);
    Logger.trace('Added sub category to category');
  };

  const handleSubCategoryNameChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].name[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleSubCategoryDescriptionChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].description[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const removeSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories.splice(subCategoryIndex, 1);
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const addNewQuestionToClosedQuestionSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleSelfAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleExternalAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const removeQuestionFromClosedQuestionSubCategory = (subCategoryIndex: number, questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewClosedQuestionDialog };
      updateDialog.subCategories[subCategoryIndex].questions.splice(questionIndex, 1);
      setAddNewClosedQuestionDialog(updateDialog);
    };
  };

  const handleAddNewClosedQuestions = async () => {
    const payload: CreateClosedQuestionPayload[] = [
      {
        name: addNewClosedQuestionDialog.categoryName,
        selfIntroduction: addNewClosedQuestionDialog.categoryInstructionSelf,
        externalIntroduction: addNewClosedQuestionDialog.categoryInstructionExternal,
        subCategories: addNewClosedQuestionDialog.subCategories,
      },
    ];
    const res = await createClosedLeadQuestion(payload);
    if (res && !res.error) {
      fetchClosedQuestions(closedQuestions, copyQuery);
      handleCloseAddNewClosedQuestionDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/closed-questions', 'post');
    }
  };

  /**
   * Handlers for adding a dimension to an existing closed question
   */

  const handleOpenAddDimensionDialog = (categoryId?: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setAddDimensionDialog({ ...addDimensionDialog, open: true, categoryId });
    };
  };

  const handleCloseAddDimensionDialog = () => {
    setAddDimensionDialog({
      open: false,
      categoryId: undefined,
      name: { de: '', en: '' },
      description: { de: '', en: '' },
      questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
    });
  };

  const handleDimensionNameChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addDimensionDialog };
      updateDialog.name[lang] = event.target.value;
      setAddDimensionDialog(updateDialog);
    };
  };

  const handleDimensionDescriptionChange = (lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addDimensionDialog };
      updateDialog.description[lang] = event.target.value;
      setAddDimensionDialog(updateDialog);
    };
  };

  const addNewQuestionToDimensionSubCategory = () => {
    const updateDialog = { ...addDimensionDialog };
    updateDialog.questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
    setAddDimensionDialog(updateDialog);
  };

  const handleDimensionSelfAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addDimensionDialog };
      updateDialog.questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddDimensionDialog(updateDialog);
    };
  };

  const handleDimensionExternalAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addDimensionDialog };
      updateDialog.questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddDimensionDialog(updateDialog);
    };
  };

  const removeQuestionFromDimension = (questionIndex: number) => {
    return () => {
      const updateDialog = { ...addDimensionDialog };
      updateDialog.questions.splice(questionIndex, 1);
      setAddDimensionDialog(updateDialog);
    };
  };

  const addClosedDimension = async () => {
    const payload: ExtendClosedQuestionPayload[] = [
      {
        id: addDimensionDialog.categoryId || '',
        subCategories: [
          {
            name: { de: addDimensionDialog.name.de, en: addDimensionDialog.name.en },
            description: { de: addDimensionDialog.description.de, en: addDimensionDialog.description.en },
            questions: addDimensionDialog.questions,
          },
        ],
      },
    ];
    const res = await addClosedCategoryDimension(payload);
    if (res && !res.error) {
      fetchClosedQuestions(closedQuestions, copyQuery);
      handleCloseAddDimensionDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/closed-questions', 'post');
    }
  };

  const addEffectDimension = async () => {
    const payload: ExtendEffectQuestionPayload[] = [
      {
        name: { de: addDimensionDialog.name.de, en: addDimensionDialog.name.en },
        questions: addDimensionDialog.questions,
      },
    ];
    const res = await addEffectCategoryDimension(payload);
    if (res && !res.error) {
      fetchEffectQuestions(effectQuestions, copyQuery);
      handleCloseAddDimensionDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/effect-questions', 'post');
    }
  };

  /**
   * Handlers for adding new effect questions
   */

  const handleOpenAddNewEffectQuestionsDialog = () => {
    setAddNewEffectQuestionsDialog({ ...addNewEffectQuestionsDialog, open: true });
  };

  const handleCloseAddNewEffectQuestionsDialog = () => {
    setAddNewEffectQuestionsDialog({
      open: false,
      subCategories: [
        {
          name: { de: '', en: '' },
          questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
        },
      ],
    });
  };

  const handleEffectSubCategoryNameChange = (subCategoryIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].name[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const handleEffectSelfAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const handleEffectExternalAssessmentQuestionChange = (subCategoryIndex: number, questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const removeQuestionFromEffectQuestionSubCategory = (subCategoryIndex: number, questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions.splice(questionIndex, 1);
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const removeEffectSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories.splice(subCategoryIndex, 1);
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const addNewQuestionToEffectQuestionSubCategory = (subCategoryIndex: number) => {
    return () => {
      const updateDialog = { ...addNewEffectQuestionsDialog };
      updateDialog.subCategories[subCategoryIndex].questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
      setAddNewEffectQuestionsDialog(updateDialog);
    };
  };

  const addNewEffectSubCategory = () => {
    const updateDialog = { ...addNewEffectQuestionsDialog };
    updateDialog.subCategories.push({
      name: { de: '', en: '' },
      questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }],
    });
    setAddNewEffectQuestionsDialog(updateDialog);
  };

  const handleAddNewEffectQuestions = async () => {
    const payload: CreateEffectQuestionPayload[] = addNewEffectQuestionsDialog.subCategories.map((subCategory) => ({
      name: { de: subCategory.name.de, en: subCategory.name.en },
      questions: subCategory.questions,
    }));
    const res = await createEffectLeadQuestions(payload);
    if (res && !res.error) {
      fetchEffectQuestions(effectQuestions, copyQuery);
      handleCloseAddNewEffectQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/effect-questions', 'post');
    }
  };

  /**
   * Handlers for adding new open questions
   */

  const handleOpenAddNewOpenQuestionsDialog = () => {
    setAddNewOpenQuestionsDialog({ ...addNewOpenQuestionsDialog, open: true });
  };

  const handleCloseAddNewOpenQuestionsDialog = () => {
    setAddNewOpenQuestionsDialog({ open: false, questions: [{ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } }] });
  };

  const addNewOpenQuestion = () => {
    const updateDialog = { ...addNewOpenQuestionsDialog };
    updateDialog.questions.push({ selfAssessment: { de: '', en: '' }, externalAssessment: { de: '', en: '' } });
    setAddNewOpenQuestionsDialog(updateDialog);
  };

  const removeOpenQuestion = (questionIndex: number) => {
    return () => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions.splice(questionIndex, 1);
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleOpenSelfAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions[questionIndex].selfAssessment[lang] = event.target.value;
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleOpenExternalAssessmentQuestionChange = (questionIndex: number, lang: QuestionnaireLanguages) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateDialog = { ...addNewOpenQuestionsDialog };
      updateDialog.questions[questionIndex].externalAssessment[lang] = event.target.value;
      setAddNewOpenQuestionsDialog(updateDialog);
    };
  };

  const handleAddNewOpenQuestions = async () => {
    const payload: i18nQuestion[] = addNewOpenQuestionsDialog.questions;
    const res = await createOpenLeadQuestions(payload);
    if (res && !res.error) {
      fetchOpenQuestions(copyQuery ? details?.openQuestions : openQuestions, copyQuery);
      handleCloseAddNewOpenQuestionsDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/lead/open-questions', 'post');
    }
  };

  /**
   * Handlers for deleting lead questions
   */

  const handleOpenConfirmDeleteLeadQuestionDialog = (questionId: string) => {
    return () => {
      setConfirmDeleteLeadQuestionDialog({ open: true, questionId });
    };
  };

  const handleCloseConfirmDeleteLeadQuestionDialog = () => {
    setConfirmDeleteLeadQuestionDialog({ open: false, questionId: '' });
  };

  const updateQuestionsAfterDelete = (id: string) => {
    const updateOpenQuestions = [...openQuestions];
    const updateSelectedOpenQuestions = [...selectedOpenQuestions];
    updateOpenQuestions.forEach((question) => {
      if (question.id === id) {
        if (updateSelectedOpenQuestions.includes(id)) {
          _.pull(updateSelectedOpenQuestions, id);
        }
        _.pull(updateOpenQuestions, question);
      }
    });
    setSelectedOpenQuestions(updateSelectedOpenQuestions);
    fetchOpenQuestions(updateOpenQuestions, copyQuery);

    const updateClosedQuestions = [...closedQuestions];
    const updateSelectedCloseQuestions = [...selectedClosedQuestions];
    updateClosedQuestions.forEach((category) => {
      category.subCategories.forEach((subCategory) => {
        subCategory.questions.forEach((question) => {
          if (question.id === id) {
            if (updateSelectedCloseQuestions.includes(id)) {
              _.pull(updateSelectedCloseQuestions, id);
            }
            _.pull(subCategory.questions, question);
          }
        });
      });
    });
    setSelectedClosedQuestions(updateSelectedCloseQuestions);
    fetchClosedQuestions(updateClosedQuestions, copyQuery);

    const updateEffectQuestions = [...effectQuestions];
    const updateSelectedEffectQuestions = [...selectedEffectQuestions];
    updateEffectQuestions.forEach((subCategory) => {
      subCategory.questions.forEach((question) => {
        if (question.id === id) {
          if (updateSelectedEffectQuestions.includes(id)) {
            _.pull(updateSelectedEffectQuestions, id);
          }
          _.pull(subCategory.questions, question);
        }
      });
    });
    setSelectedEffectQuestions(updateSelectedEffectQuestions);
    fetchEffectQuestions(updateEffectQuestions, copyQuery);
  };

  const updateQuestionsWithoutDelete = (id: string) => {
    const updateOpenQuestions = [...openQuestions];
    const updateSelectedOpenQuestions = [...selectedOpenQuestions];
    updateOpenQuestions.forEach((question) => {
      if (question.id === id) {
        if (updateSelectedOpenQuestions.includes(id)) {
          _.pull(updateSelectedOpenQuestions, id);
        }
      }
    });
    setSelectedOpenQuestions(updateSelectedOpenQuestions);

    const updateClosedQuestions = [...closedQuestions];
    const updateSelectedCloseQuestions = [...selectedClosedQuestions];
    updateClosedQuestions.forEach((category) => {
      category.subCategories.forEach((subCategory) => {
        subCategory.questions.forEach((question) => {
          if (question.id === id) {
            if (updateSelectedCloseQuestions.includes(id)) {
              _.pull(updateSelectedCloseQuestions, id);
            }
          }
        });
      });
    });
    setSelectedClosedQuestions(updateSelectedCloseQuestions);

    const updateEffectQuestions = [...effectQuestions];
    const updateSelectedEffectQuestions = [...selectedEffectQuestions];
    updateEffectQuestions.forEach((subCategory) => {
      subCategory.questions.forEach((question) => {
        if (question.id === id) {
          if (updateSelectedEffectQuestions.includes(id)) {
            _.pull(updateSelectedEffectQuestions, id);
          }
        }
      });
    });
    setSelectedEffectQuestions(updateSelectedEffectQuestions);
  };

  const handleDeleteLeadQuestion = (id: string) => {
    return async () => {
      if (!copyQuery) {
        const res = await deleteLeadQuestion(id);
        if (!res.error) {
          updateQuestionsAfterDelete(id);
          handleCloseConfirmDeleteLeadQuestionDialog();
        } else {
          handleOpenErrorDialog(res?.error, '/lead/questions/:questionId', 'delete');
        }
      } else {
        updateQuestionsWithoutDelete(id);
        handleCloseConfirmDeleteLeadQuestionDialog();
      }
    };
  };

  /**
   * Handlers for deleting lead categories
   */

  const handleOpenConfirmDeleteLeadCategoryDialog = (categoryId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      setConfirmDeleteLeadCategoryDialog({ open: true, categoryId });
    };
  };

  const handleCloseConfirmDeleteLeadCategoryDialog = () => {
    setConfirmDeleteLeadCategoryDialog({ open: false, categoryId: '' });
  };

  const handleDeleteLeadCategory = (id: string) => {
    return async () => {
      const res = await deleteLeadCategory(id);
      if (!res.error) {
        const updateClosedQuestions = [...closedQuestions];
        const updateSelectedCloseQuestions = [...selectedClosedQuestions];
        updateClosedQuestions.forEach((category) => {
          if (category.id === id) {
            category.subCategories.forEach((subCategory) => {
              subCategory.questions.forEach((question) => {
                if (updateSelectedCloseQuestions.includes(question.id)) {
                  _.pull(updateSelectedCloseQuestions, question.id);
                }
              });
            });
            _.pull(updateClosedQuestions, category);
          }
          category.subCategories.forEach((subCategory) => {
            if (subCategory.id === id) {
              subCategory.questions.forEach((question) => {
                if (updateSelectedCloseQuestions.includes(question.id)) {
                  _.pull(updateSelectedCloseQuestions, question.id);
                }
              });
              _.pull(category.subCategories, subCategory);
            }
          });
        });
        setSelectedClosedQuestions(updateSelectedCloseQuestions);
        fetchClosedQuestions(updateClosedQuestions, copyQuery);

        const updateEffectQuestions = [...effectQuestions];
        const updateSelectedEffectQuestions = [...selectedEffectQuestions];
        updateEffectQuestions.forEach((subCategory) => {
          if (subCategory.id === id) {
            subCategory.questions.forEach((question) => {
              if (updateSelectedEffectQuestions.includes(question.id)) {
                _.pull(updateSelectedEffectQuestions, question.id);
              }
            });
            _.pull(updateEffectQuestions, subCategory);
          }
        });
        setSelectedEffectQuestions(updateSelectedEffectQuestions);
        fetchEffectQuestions(updateEffectQuestions, copyQuery);

        handleCloseConfirmDeleteLeadCategoryDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/lead/categories/:categoryId', 'delete');
      }
    };
  };

  /**
   * Handlers for moving categories
   */

  const arrayMove = (arr: any[], fromIndex: number, toIndex: number) => {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  };

  const moveClosedCategory = (type: 'down' | 'up', categoryId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const categories = [...closedQuestions];
      const findCategoryIndex = categories.findIndex((cat) => cat.id === categoryId);
      if (type === 'down' && findCategoryIndex !== undefined && findCategoryIndex !== categories.length - 1) {
        arrayMove(categories, findCategoryIndex, findCategoryIndex + 1);
      }
      if (type === 'up' && findCategoryIndex !== undefined && findCategoryIndex !== 0) {
        arrayMove(categories, findCategoryIndex, findCategoryIndex - 1);
      }
      setClosedQuestions(categories);
      setPreventExpansion(true);
    };
  };

  const moveEffectCategory = (type: 'down' | 'up', categoryId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const categories = [...effectQuestions];
      const findCategoryIndex = categories.findIndex((cat) => cat.id === categoryId);
      if (type === 'down' && findCategoryIndex !== undefined && findCategoryIndex !== categories.length - 1) {
        arrayMove(categories, findCategoryIndex, findCategoryIndex + 1);
      }
      if (type === 'up' && findCategoryIndex !== undefined && findCategoryIndex !== 0) {
        arrayMove(categories, findCategoryIndex, findCategoryIndex - 1);
      }
      setEffectQuestions(categories);
      setPreventExpansion(true);
    };
  };

  const moveCompetence = (type: 'down' | 'up', categoryId: string, subCategoryId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const categories = [...closedQuestions];
      const findCategoryIndex = categories.findIndex((cat) => cat.id === categoryId);
      const findSubCategoryIndex = categories[findCategoryIndex].subCategories.findIndex((cat) => cat.id === subCategoryId);
      if (
        type === 'down' &&
        findCategoryIndex !== undefined &&
        findSubCategoryIndex !== undefined &&
        findSubCategoryIndex !== categories[findCategoryIndex].subCategories.length - 1
      ) {
        arrayMove(categories[findCategoryIndex].subCategories, findSubCategoryIndex, findSubCategoryIndex + 1);
      }
      if (type === 'up' && findCategoryIndex !== undefined && findSubCategoryIndex !== undefined && findSubCategoryIndex !== 0) {
        arrayMove(categories[findCategoryIndex].subCategories, findSubCategoryIndex, findSubCategoryIndex - 1);
      }
      setClosedQuestions(categories);
      setPreventExpansion(true);
    };
  };

  const moveClosedQuestion = (type: 'down' | 'up', categoryId: string, subCategoryId: string, questionId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const categories = [...closedQuestions];
      const findCategoryIndex = categories.findIndex((cat) => cat.id === categoryId);
      const findSubCategoryIndex = categories[findCategoryIndex].subCategories.findIndex((cat) => cat.id === subCategoryId);
      const findQuestionIndex = categories[findCategoryIndex].subCategories[findSubCategoryIndex].questions.findIndex(
        (question) => question.id === questionId
      );
      if (
        type === 'down' &&
        findCategoryIndex !== undefined &&
        findSubCategoryIndex !== undefined &&
        findQuestionIndex !== undefined &&
        findQuestionIndex !== categories[findCategoryIndex].subCategories[findSubCategoryIndex].questions.length - 1
      ) {
        arrayMove(categories[findCategoryIndex].subCategories[findSubCategoryIndex].questions, findQuestionIndex, findQuestionIndex + 1);
      }
      if (
        type === 'up' &&
        findCategoryIndex !== undefined &&
        findSubCategoryIndex !== undefined &&
        findQuestionIndex !== undefined &&
        findQuestionIndex !== 0
      ) {
        arrayMove(categories[findCategoryIndex].subCategories[findSubCategoryIndex].questions, findQuestionIndex, findQuestionIndex - 1);
      }
      setClosedQuestions(categories);
      setPreventExpansion(true);
    };
  };

  const moveEffectQuestion = (type: 'down' | 'up', subCategoryId: string, questionId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const categories = [...effectQuestions];
      const findSubCategoryIndex = categories.findIndex((cat) => cat.id === subCategoryId);
      const findQuestionIndex = categories[findSubCategoryIndex].questions.findIndex((question) => question.id === questionId);
      if (
        type === 'down' &&
        findSubCategoryIndex !== undefined &&
        findQuestionIndex !== undefined &&
        findQuestionIndex !== categories[findSubCategoryIndex].questions.length - 1
      ) {
        arrayMove(categories[findSubCategoryIndex].questions, findQuestionIndex, findQuestionIndex + 1);
      }
      if (type === 'up' && findSubCategoryIndex !== undefined && findQuestionIndex !== undefined && findQuestionIndex !== 0) {
        arrayMove(categories[findSubCategoryIndex].questions, findQuestionIndex, findQuestionIndex - 1);
      }
      setEffectQuestions(categories);
      setPreventExpansion(true);
    };
  };

  const moveOpenQuestion = (type: 'down' | 'up', questionId: string) => {
    return (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const questions = [...openQuestions];
      const findQuestionIndex = questions.findIndex((question) => question.id === questionId);
      if (type === 'down' && findQuestionIndex !== undefined && findQuestionIndex !== questions.length - 1) {
        arrayMove(questions, findQuestionIndex, findQuestionIndex + 1);
      }
      if (type === 'up' && findQuestionIndex !== undefined && findQuestionIndex !== 0) {
        arrayMove(questions, findQuestionIndex, findQuestionIndex - 1);
      }
      setOpenQuestions(questions);
      setPreventExpansion(true);
    };
  };

  return (
    <>
      {loading ? (
        <PaiDotLoader />
      ) : isNew || copyQuery ? (
        <QuestionnaireCreationView
          newName={newName}
          expanded={expanded}
          closedQuestions={closedQuestions}
          selectedClosedQuestions={selectedClosedQuestions}
          openQuestions={openQuestions}
          selectedOpenQuestions={selectedOpenQuestions}
          effectQuestions={effectQuestions}
          selectedEffectQuestions={selectedEffectQuestions}
          participantsToAdd={participantsToAdd}
          handleSaveTag={handleSaveTag}
          handleToggleTreeNode={handleToggleTreeNode}
          handleSelectionClosedQuestionsChange={handleSelectionClosedQuestionsChange}
          handleSelectionOpenQuestionsChange={handleSelectionOpenQuestionsChange}
          handleSelectionEffectQuestionsChange={handleSelectionEffectQuestionsChange}
          handleOpenAddQuestionsDialog={handleOpenAddQuestionsDialog}
          handleOpenAddParticipantsDialog={handleOpenAddParticipantsDialog}
          getRealPerspective={getRealPerspective}
          handleOpenAddNewClosedQuestionDialog={handleOpenAddNewClosedQuestionDialog}
          handleOpenAddNewEffectQuestionsDialog={handleOpenAddNewEffectQuestionsDialog}
          handleOpenAddNewOpenQuestionsDialog={handleOpenAddNewOpenQuestionsDialog}
          handleOpenConfirmDeleteLeadQuestionDialog={handleOpenConfirmDeleteLeadQuestionDialog}
          handleOpenConfirmDeleteLeadCategoryDialog={handleOpenConfirmDeleteLeadCategoryDialog}
          handleOpenAddDimensionDialog={handleOpenAddDimensionDialog}
          moveClosedCategory={moveClosedCategory}
          moveCompetence={moveCompetence}
          moveClosedQuestion={moveClosedQuestion}
          moveEffectCategory={moveEffectCategory}
          moveEffectQuestion={moveEffectQuestion}
          moveOpenQuestion={moveOpenQuestion}
        />
      ) : details ? (
        <QuestionnaireDetailsView
          details={details}
          handleOpenDuplicateQuestionnaireDialog={handleOpenDuplicateQuestionnaireDialog}
          handleOpenAddParticipantsDialog={handleOpenAddParticipantsDialog}
          copyToClipboard={copyToClipboard}
          copyToClipboardAnonymous={copyToClipboardAnonymous}
          getRealPerspective={getRealPerspective}
          handleEditParticipantIsSuspended={handleEditParticipantIsSuspended}
          handleEditParticipantRole={handleEditParticipantRole}
        />
      ) : null}

      <AddParticipantsDialog
        open={addParticipantsDialog.open}
        participants={addParticipantsDialog.participants}
        participantTrigger={participantTrigger}
        formChangeHandlerGenerator={formChangeHandlerGenerator}
        formChangeHandleDeleteGenerator={formChangeHandleDeleteGenerator}
        addEvaluator={addParticipant}
        handleClose={handleCloseAddParticipantsDialog}
        handleProceed={handleAddParticipants}
      />
      <CreateQuestionnaireDialog
        open={createQuestionnaireDialog.open}
        name={createQuestionnaireDialog.name}
        handleTextFieldChange={handleCreateQuestionnaireTextfieldChange}
        handleClose={handleCloseCreateQuestionnaireDialog}
        handleProceed={handleEnterName}
      />
      <CloneQuestionnaireDialog
        open={duplicateQuestionnaireDialog.open}
        name={duplicateQuestionnaireDialog.name}
        handleTextFieldChange={handleDuplicateQuestionnaireDialogTextFieldChange}
        handleClose={handleCloseDuplicateQuestionnaireDialog}
        handleProceed={duplicateQuestionnaire}
      />

      <AddQuestionsDialog
        open={addQuestionsDialog.open}
        selfAssessment={addQuestionsDialog.selfAssessment}
        externalAssessment={addQuestionsDialog.externalAssessment}
        handleTextFieldChange={handleAddQuestionDialogTextFieldChange}
        handleClose={handleCloseAddQuestionsDialog}
        handleProceed={addQuestionsDialog.categoryId ? handleAddQuestionToClosedCategory : handleAddQuestionToEffectCategory}
      />
      <AddDimensionDialog
        open={addDimensionDialog.open}
        categoryId={addDimensionDialog.categoryId}
        name={addDimensionDialog.name}
        description={addDimensionDialog.description}
        questions={addDimensionDialog.questions}
        handleDimensionDescriptionChange={handleDimensionDescriptionChange}
        handleDimensionNameChange={handleDimensionNameChange}
        addNewQuestionToDimensionSubCategory={addNewQuestionToDimensionSubCategory}
        handleDimensionSelfAssessmentQuestionChange={handleDimensionSelfAssessmentQuestionChange}
        handleDimensionExternalAssessmentQuestionChange={handleDimensionExternalAssessmentQuestionChange}
        removeQuestionFromDimension={removeQuestionFromDimension}
        handleClose={handleCloseAddDimensionDialog}
        addDimension={addDimensionDialog.categoryId ? addClosedDimension : addEffectDimension}
      />
      <AddNewOpenQuestionsDialog
        open={addNewOpenQuestionsDialog.open}
        questions={addNewOpenQuestionsDialog.questions}
        handleClose={handleCloseAddNewOpenQuestionsDialog}
        handleProceed={handleAddNewOpenQuestions}
        addNewOpenQuestion={addNewOpenQuestion}
        removeOpenQuestion={removeOpenQuestion}
        handleOpenSelfAssessmentQuestionChange={handleOpenSelfAssessmentQuestionChange}
        handleOpenExternalAssessmentQuestionChange={handleOpenExternalAssessmentQuestionChange}
      />
      <AddNewEffectQuestionsDialog
        open={addNewEffectQuestionsDialog.open}
        subCategories={addNewEffectQuestionsDialog.subCategories}
        handleClose={handleCloseAddNewEffectQuestionsDialog}
        handleProceed={handleAddNewEffectQuestions}
        handleEffectSubCategoryNameChange={handleEffectSubCategoryNameChange}
        handleEffectSelfAssessmentQuestionChange={handleEffectSelfAssessmentQuestionChange}
        handleEffectExternalAssessmentQuestionChange={handleEffectExternalAssessmentQuestionChange}
        removeQuestionFromEffectQuestionSubCategory={removeQuestionFromEffectQuestionSubCategory}
        removeEffectSubCategory={removeEffectSubCategory}
        addNewQuestionToEffectQuestionSubCategory={addNewQuestionToEffectQuestionSubCategory}
        addNewEffectSubCategory={addNewEffectSubCategory}
      />
      <AddNewClosedQuestionDialog
        open={addNewClosedQuestionDialog.open}
        categoryName={addNewClosedQuestionDialog.categoryName}
        categoryInstructionSelf={addNewClosedQuestionDialog.categoryInstructionSelf}
        categoryInstructionExternal={addNewClosedQuestionDialog.categoryInstructionExternal}
        subCategories={addNewClosedQuestionDialog.subCategories}
        handleClose={handleCloseAddNewClosedQuestionDialog}
        handleProceed={handleAddNewClosedQuestions}
        handleCategoryNameChange={handleCategoryNameChange}
        handleCategoryInstructionSelfChange={handleCategoryInstructionSelfChange}
        handleCategoryInstructionExternalChange={handleCategoryInstructionExternalChange}
        addNewSubCategory={addNewSubCategory}
        addNewQuestionToClosedQuestionSubCategory={addNewQuestionToClosedQuestionSubCategory}
        removeQuestionFromClosedQuestionSubCategory={removeQuestionFromClosedQuestionSubCategory}
        removeSubCategory={removeSubCategory}
        handleSubCategoryNameChange={handleSubCategoryNameChange}
        handleSubCategoryDescriptionChange={handleSubCategoryDescriptionChange}
        handleSelfAssessmentQuestionChange={handleSelfAssessmentQuestionChange}
        handleExternalAssessmentQuestionChange={handleExternalAssessmentQuestionChange}
      />

      <ConfirmDeleteQuestionDialog
        open={confirmDeleteLeadQuestionDialog.open}
        handleClose={handleCloseConfirmDeleteLeadQuestionDialog}
        handleConfirm={handleDeleteLeadQuestion(confirmDeleteLeadQuestionDialog.questionId)}
      />
      <ConfirmDeleteCategoryDialog
        open={confirmDeleteLeadCategoryDialog.open}
        handleClose={handleCloseConfirmDeleteLeadCategoryDialog}
        handleConfirm={handleDeleteLeadCategory(confirmDeleteLeadCategoryDialog.categoryId)}
      />
      <Snackbar open={snackbarOpen} message={<span>{t('keyCopied', { key: tagId })}</span>} />
    </>
  );
};

export default LeadQuestionnaireDetails;
